import { useCallback, useMemo, useState } from 'react'
import { useStore } from 'react-redux'
import { useAppDispatch } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { selectLoadedUserFilesById, selectFilesForFolder } from '@/user_files/files/store/selectors'
import { isExternalStorageSyncedFolder, selectChildFolders, selectFoldersRecord } from '@/user_files/folders/store/selectors'
import { selectIsUserFilesOperationActive } from '@/user_files/batching/store/selectors'
import { resolveUploadName } from '@/user_files/drive/utils/resolve-upload-name'
import { renameFile } from '@/user_files/files/fetch/patch-rename-file'
import { renameFolder } from '@/user_files/folders/fetch/patch-rename-folder'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { UserFilesBatchingActions } from '@/user_files/batching/store/slice'
import { UppyFileUploadActions } from '@/uppy-file-upload/store/slice'
import type { FolderResponse } from '@/user_files/folders/schemas'
import type {
  FileRenamePrecondition,
  FileSelectionPrecondition,
  FolderPathPrecondition,
  FolderRenamePrecondition,
  FolderSelectionPrecondition,
} from '@/user_files/schemas/preconditions'
import { getUserFilesErrorMessage, shouldRefreshAfterUserFilesError } from '@/user_files/utils/api-error'
import {
  buildFileRenamePrecondition,
  buildFileSelectionPrecondition,
  buildFolderPathPrecondition,
  buildFolderRenamePrecondition,
  buildFolderSelectionPrecondition,
  selectPreconditionsById,
} from '@/user_files/utils/preconditions'
import type { Conflict } from '@/user_files/drive/components/ConflictModal'

type PendingMoveConflict = Conflict & {
  id: string
  type: 'file' | 'folder'
  fileRenamePrecondition?: FileRenamePrecondition
  folderRenamePrecondition?: FolderRenamePrecondition
}

type PendingMove = {
  fileIds: string[]
  folderIds: string[]
  sourceFolderId: string
  destinationFolderId: string
  filePreconditions?: FileSelectionPrecondition[]
  folderPreconditions?: FolderSelectionPrecondition[]
  destinationFolderPrecondition?: FolderPathPrecondition
}

type CheckAndConfirmMoveArgs = {
  fileIds: string[]
  folderIds: string[]
  sourceFolderId: string
  destinationFolderId: string
  filePreconditions?: FileSelectionPrecondition[]
  folderPreconditions?: FolderSelectionPrecondition[]
}

type UseMoveConflictReturn = {
  /**
   * Call when the user picks a destination. Dispatches the move directly when
   * there are no conflicts; stores the pending move and opens the conflict modal otherwise.
   */
  checkAndConfirmMove: (args: CheckAndConfirmMoveArgs) => void
  /** State and handlers for the move conflict modal. */
  moveConflictDialog: {
    /** Whether the conflict modal is open. */
    open: boolean
    /** One entry per file/folder whose name collides with an existing item in the destination. */
    conflicts: Conflict[]
    /** Names already taken in the destination folder — used for per-field validation in the modal. */
    occupiedNames: ReadonlySet<string>
    /** Renames the conflicting items via the API then dispatches the stored move. */
    onConfirm: (resolvedNames: string[]) => void
    /** Discards the pending move without renaming anything. */
    onCancel: () => void
    /** True while the rename API calls are in flight. */
    isSubmitting: boolean
    /** Inline footer error from a failed rename or a blocked move dispatch. */
    error: string | null
  }
}

export function useMoveConflict({ onComplete }: { onComplete: () => void }): UseMoveConflictReturn {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()

  const [pendingConflicts, setPendingConflicts] = useState<PendingMoveConflict[]>([])
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false)
  const [occupiedNamesSnapshot, setOccupiedNamesSnapshot] = useState<ReadonlySet<string>>(new Set())
  const [pendingMove, setPendingMove] = useState<PendingMove | null>(null)
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameError, setRenameError] = useState<string | null>(null)

  const dispatchMove = useCallback(
    (move: PendingMove): boolean => {
      if (selectIsUserFilesOperationActive(store.getState())) return false
      dispatch(
        UserFilesBatchingActions.enqueueMoveSelection({
          sourceFolderId: move.sourceFolderId,
          destinationFolderId: move.destinationFolderId,
          fileIds: move.fileIds,
          folderIds: move.folderIds,
          filePreconditions: selectPreconditionsById(move.filePreconditions, move.fileIds),
          folderPreconditions: selectPreconditionsById(move.folderPreconditions, move.folderIds),
          destinationFolderPrecondition: move.destinationFolderPrecondition,
        })
      )
      onComplete()
      return true
    },
    [dispatch, store, onComplete]
  )

  const checkAndConfirmMove = useCallback(
    ({ fileIds, folderIds, sourceFolderId, destinationFolderId, filePreconditions, folderPreconditions }: CheckAndConfirmMoveArgs): void => {
      const state = store.getState()
      const filesById = selectLoadedUserFilesById(state)
      const foldersById = selectFoldersRecord(state)
      const destinationFolder = foldersById[destinationFolderId]?.data
      const destinationFolderPrecondition = destinationFolder ? buildFolderPathPrecondition(destinationFolder) : undefined
      const resolvedFilePreconditions =
        filePreconditions ??
        fileIds
          .map((fileId) => filesById[fileId])
          .filter((file): file is NonNullable<(typeof filesById)[string]> => Boolean(file))
          .map(buildFileSelectionPrecondition)
      const resolvedFolderPreconditions =
        folderPreconditions ??
        folderIds
          .map((folderId) => foldersById[folderId]?.data)
          .filter((folder): folder is FolderResponse => Boolean(folder))
          .map(buildFolderSelectionPrecondition)

      const destFiles = selectFilesForFolder(state, { folderId: destinationFolderId })
      const destChildFolders = selectChildFolders(state, { parentId: destinationFolderId })
      // exclude DMS folders from the existing folder names
      const occupiedNames = new Set<string>([
        ...destFiles.filter((f) => !f.is_deleted).map((f) => f.original_filename),
        ...destChildFolders.filter((folder) => !isExternalStorageSyncedFolder(folder)).map((folder) => folder.name),
      ])

      const conflicts: PendingMoveConflict[] = []

      for (const fileId of fileIds) {
        const file = filesById[fileId]
        if (!file) continue
        if (occupiedNames.has(file.original_filename)) {
          const resolvedName = resolveUploadName(file.original_filename, occupiedNames)
          occupiedNames.add(resolvedName)
          conflicts.push({
            id: fileId,
            originalName: file.original_filename,
            resolvedName,
            type: 'file',
            fileRenamePrecondition: buildFileRenamePrecondition(file),
          })
        }
      }

      for (const folderId of folderIds) {
        const folder = foldersById[folderId]
        if (!folder) continue
        const folderName = folder.data.name
        if (occupiedNames.has(folderName)) {
          const resolvedName = resolveUploadName(folderName, occupiedNames, 'folder')
          occupiedNames.add(resolvedName)
          conflicts.push({
            id: folderId,
            originalName: folderName,
            resolvedName,
            type: 'folder',
            folderRenamePrecondition: buildFolderRenamePrecondition(folder.data),
          })
        }
      }

      if (conflicts.length === 0) {
        dispatchMove({
          fileIds,
          folderIds,
          sourceFolderId,
          destinationFolderId,
          filePreconditions: resolvedFilePreconditions,
          folderPreconditions: resolvedFolderPreconditions,
          destinationFolderPrecondition,
        })
        return
      }

      const baseOccupied = new Set(occupiedNames)
      for (const c of conflicts) baseOccupied.delete(c.resolvedName)

      setPendingConflicts(conflicts)
      setOccupiedNamesSnapshot(baseOccupied)
      setPendingMove({
        fileIds,
        folderIds,
        sourceFolderId,
        destinationFolderId,
        filePreconditions: resolvedFilePreconditions,
        folderPreconditions: resolvedFolderPreconditions,
        destinationFolderPrecondition,
      })
      setConflictDialogOpen(true)
    },
    [store, dispatchMove]
  )

  const confirmMoveConflict = useCallback(
    async (resolvedNames: string[]) => {
      if (!pendingMove) return
      setIsRenaming(true)
      setRenameError(null)
      try {
        const results = await Promise.allSettled(
          pendingConflicts.map(async (conflict, i) => {
            const newName = resolvedNames[i] ?? conflict.resolvedName
            if (conflict.type === 'folder') {
              const updated = await renameFolder(conflict.id, { name: newName, precondition: conflict.folderRenamePrecondition })
              dispatch(UserFoldersActions.upsertFolder(updated))
            } else {
              const updated = await renameFile(conflict.id, { filename: newName, precondition: conflict.fileRenamePrecondition })
              dispatch(UserFilesActions.upsertUserFiles([updated]))
              dispatch(UppyFileUploadActions.fileRenamedByServerRecord(updated))
            }
          })
        )

        const failedIndices = results.flatMap((r, i) => (r.status === 'rejected' ? [i] : []))
        if (failedIndices.length > 0) {
          const failedConflicts = failedIndices.map((i) => pendingConflicts[i])
          const failedIds = new Set(failedConflicts.map((c) => c.id))

          // Move everything that was successfully renamed (plus non-conflicting items)
          const moveNow = {
            ...pendingMove,
            fileIds: pendingMove.fileIds.filter((id) => !failedIds.has(id)),
            folderIds: pendingMove.folderIds.filter((id) => !failedIds.has(id)),
            filePreconditions: pendingMove.filePreconditions?.filter((precondition) => !failedIds.has(precondition.id)),
            folderPreconditions: pendingMove.folderPreconditions?.filter((precondition) => !failedIds.has(precondition.id)),
          }
          if (moveNow.fileIds.length > 0 || moveNow.folderIds.length > 0) {
            dispatchMove(moveNow)
          }

          const firstError = results[failedIndices[0]] as PromiseRejectedResult
          console.error('Failed to rename items before moving:', firstError.reason)
          if (shouldRefreshAfterUserFilesError(firstError.reason)) {
            dispatch(UserFoldersActions.fetchFolderChildren({ folderId: pendingMove.sourceFolderId, force: true }))
            dispatch(UserFilesActions.fetchFilesForFolder({ folderId: pendingMove.sourceFolderId }))
            dispatch(UserFoldersActions.fetchFolderChildren({ folderId: pendingMove.destinationFolderId, force: true }))
            dispatch(UserFilesActions.fetchFilesForFolder({ folderId: pendingMove.destinationFolderId }))
          }
          setPendingConflicts(failedConflicts.map((c, idx) => ({ ...c, resolvedName: resolvedNames[failedIndices[idx]] ?? c.resolvedName })))
          setPendingMove({
            ...pendingMove,
            fileIds: failedConflicts.filter((c) => c.type === 'file').map((c) => c.id),
            folderIds: failedConflicts.filter((c) => c.type === 'folder').map((c) => c.id),
            filePreconditions: pendingMove.filePreconditions?.filter((precondition) => failedIds.has(precondition.id)),
            folderPreconditions: pendingMove.folderPreconditions?.filter((precondition) => failedIds.has(precondition.id)),
          })
          setRenameError(getUserFilesErrorMessage(firstError.reason, 'Failed to rename items before moving. Please try again.'))
          return
        }

        const moved = dispatchMove(pendingMove)
        if (!moved) {
          setRenameError('Another operation is in progress. Please try again.')
          return
        }
        setConflictDialogOpen(false)
        setPendingConflicts([])
        setPendingMove(null)
      } finally {
        setIsRenaming(false)
      }
    },
    [pendingConflicts, pendingMove, dispatchMove, dispatch]
  )

  const cancelMoveConflict = useCallback(() => {
    setConflictDialogOpen(false)
    setPendingConflicts([])
    setPendingMove(null)
    setRenameError(null)
  }, [])

  const conflictDialogConflicts = useMemo(
    () => pendingConflicts.map(({ originalName, resolvedName, type }) => ({ originalName, resolvedName, type })),
    [pendingConflicts]
  )

  return {
    checkAndConfirmMove,
    moveConflictDialog: {
      open: conflictDialogOpen,
      conflicts: conflictDialogConflicts,
      occupiedNames: occupiedNamesSnapshot,
      onConfirm: confirmMoveConflict,
      onCancel: cancelMoveConflict,
      isSubmitting: isRenaming,
      error: renameError,
    },
  }
}
